import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { UserPlus, Loader2, KanbanSquare, Check } from "lucide-react";

import { Button } from "../ui/button";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "../ui/tooltip";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { Label } from "../ui/label";

import useLeadStages from "../../hooks/useLeadStages";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { cn } from "../../lib/utils";

const STATUS_PADRAO = [
	{ slug: "new", name: "Novo", color: "#3b82f6" },
	{ slug: "contacted", name: "Contatado", color: "#eab308" },
	{ slug: "qualified", name: "Qualificado", color: "#a855f7" },
	{ slug: "won", name: "Convertido", color: "#22c55e" },
	{ slug: "lost", name: "Perdido", color: "#ef4444" },
];

// Vira lead o contato desta conversa, permitindo escolher em qual coluna do quadro ele entra.
const ConverterEmLeadButton = ({ ticket }) => {
	const [modalOpen, setModalOpen] = useState(false);
	const [convertendo, setConvertendo] = useState(false);
	const [etapaSelecionada, setEtapaSelecionada] = useState("new");
	const [pipelineSelecionado, setPipelineSelecionado] = useState("");
	const [pipelines, setPipelines] = useState([]);

	const { etapas, carregando: carregandoEtapas } = useLeadStages();
	const listaEtapas = etapas && etapas.length > 0 ? etapas : STATUS_PADRAO;

	const contato = ticket?.contact || {};
	const nomeContato = contato.name || contato.number || "Contato";
	const numeroContato = contato.number || "";

	// Carrega pipelines caso haja mais de um funil cadastrado
	useEffect(() => {
		if (modalOpen) {
			(async () => {
				try {
					const { data } = await api.get("/crm/lead-pipelines");
					const lista = Array.isArray(data) ? data : [];
					setPipelines(lista);
					if (lista.length > 0 && !pipelineSelecionado) {
						setPipelineSelecionado(String(lista[0].id));
					}
				} catch (err) {
					// Usa funil padrão
				}
			})();
		}
	}, [modalOpen]);

	// Atualiza etapa inicial quando as etapas carregarem
	useEffect(() => {
		if (listaEtapas.length > 0 && !etapaSelecionada) {
			setEtapaSelecionada(listaEtapas[0].slug);
		}
	}, [listaEtapas]);

	// Grupo não vira lead: a negociação é com uma pessoa, não com a sala.
	if (!ticket?.id || ticket.isGroup) return null;

	const handleOpen = () => {
		if (listaEtapas.length > 0 && !etapaSelecionada) {
			setEtapaSelecionada(listaEtapas[0].slug);
		}
		setModalOpen(true);
	};

	const converter = async () => {
		setConvertendo(true);
		try {
			const etapaObj = listaEtapas.find((e) => e.slug === etapaSelecionada);
			const nomeEtapa = etapaObj?.name || "Novo";

			const { data } = await api.post("/crm/leads/from-contact", {
				contactId: ticket.contactId || ticket.contact?.id,
				ticketId: ticket.id,
				source: "atendimento",
				status: etapaSelecionada,
				leadPipelineId: pipelineSelecionado ? Number(pipelineSelecionado) : undefined,
			});

			toast.success(
				data?.jaExistia
					? `Lead atualizado para a coluna "${nomeEtapa}" no quadro.`
					: `Lead adicionado na coluna "${nomeEtapa}" do quadro.`,
			);
			setModalOpen(false);
		} catch (err) {
			toastError(err);
		}
		setConvertendo(false);
	};

	return (
		<>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							variant="ghost"
							size="icon"
							onClick={handleOpen}
							aria-label="Converter em lead"
						>
							<UserPlus className="h-4 w-4" />
						</Button>
					</TooltipTrigger>
					<TooltipContent>Converter em lead</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Dialog open={modalOpen} onOpenChange={setModalOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<KanbanSquare className="h-5 w-5 text-primary" />
							Adicionar ao Quadro de Leads
						</DialogTitle>
						<DialogDescription>
							Selecione em qual coluna do quadro o contato <strong>{nomeContato}</strong> {numeroContato ? `(${numeroContato})` : ""} deve se encontrar.
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-4 py-2">
						{pipelines.length > 1 && (
							<div className="space-y-1.5">
								<Label>Funil de Vendas</Label>
								<Select
									value={pipelineSelecionado}
									onValueChange={setPipelineSelecionado}
								>
									<SelectTrigger>
										<SelectValue placeholder="Selecione o funil" />
									</SelectTrigger>
									<SelectContent>
										{pipelines.map((p) => (
											<SelectItem key={p.id} value={String(p.id)}>
												{p.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						)}

						<div className="space-y-2">
							<Label>Coluna / Etapa do Quadro</Label>
							<div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto pr-1">
								{listaEtapas.map((etapa) => {
									const selecionado = etapaSelecionada === etapa.slug;
									const cor = etapa.color || "#3b82f6";

									return (
										<button
											key={etapa.slug}
											type="button"
											onClick={() => setEtapaSelecionada(etapa.slug)}
											className={cn(
												"flex items-center justify-between rounded-xl border p-3 text-left transition-all",
												selecionado
													? "border-primary bg-primary/5 ring-2 ring-primary/20 shadow-xs"
													: "border-border bg-card hover:bg-accent hover:border-border/80",
											)}
										>
											<div className="flex items-center gap-2.5">
												<span
													className="h-3.5 w-3.5 rounded-full shrink-0 shadow-xs"
													style={{ backgroundColor: cor }}
												/>
												<span className="text-sm font-medium leading-none">
													{etapa.name}
												</span>
											</div>

											{selecionado && (
												<Check className="h-4 w-4 text-primary shrink-0" />
											)}
										</button>
									);
								})}
							</div>
						</div>
					</div>

					<DialogFooter className="gap-2 sm:gap-0">
						<Button
							type="button"
							variant="outline"
							onClick={() => setModalOpen(false)}
							disabled={convertendo}
						>
							Cancelar
						</Button>
						<Button
							type="button"
							onClick={converter}
							disabled={convertendo || carregandoEtapas}
						>
							{convertendo ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<UserPlus className="h-4 w-4" />
							)}
							Salvar no Quadro
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default ConverterEmLeadButton;
